// ============================================================================
// DashboardPage — the trainer's home, FULLY wired to the real API.
//   • calendar from GET /api/calendar
//   • KPIs + roster from GET /api/clients + the calendar feed
//   • progress snapshot from GET /api/clients/{id}/metrics
// Data flows through <DashboardDataProvider/> (useDashboardData); the locked
// Pulse sections render it unchanged. Live-sync invalidations (see App) make
// the whole page refresh across devices without a reload.
// ============================================================================
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Badge, Banner } from "../components";
import { useAuth } from "../auth/AuthContext";
import { KpiRow } from "../dashboard/KpiRow";
import { PackageReminderStrip } from "../dashboard/PackageReminderStrip";
import { SessionsCalendar } from "../dashboard/SessionsCalendar";
import { UpcomingSessions } from "../dashboard/UpcomingSessions";
import { ClientRoster } from "../dashboard/ClientRoster";
import { InactiveClientsCard } from "../dashboard/InactiveClients";
import { ProgressSnapshot } from "../dashboard/ProgressSnapshot";
import {
  DashboardDataProvider,
  useDashboardCtx,
  sessionsOnDay,
} from "../dashboard/DashboardDataContext";
import { ShareProfileCard } from "../profile/ShareProfile";
import { useAccount } from "../hooks";
import { LoadingBlock, ErrorBlock } from "./PageState";

export function DashboardPage() {
  return (
    <DashboardDataProvider>
      <DashboardInner />
    </DashboardDataProvider>
  );
}

function greetingKey(): string {
  const h = new Date().getHours();
  if (h < 12) return "dashboard.greetingMorning";
  if (h < 18) return "dashboard.greetingAfternoon";
  return "dashboard.greetingEvening";
}

function DashboardInner() {
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  const { trainer } = useAuth();
  const account = useAccount();
  const data = useDashboardCtx();

  const firstName =
    (trainer?.display_name ?? trainer?.username ?? "").split(" ")[0] || "";
  const todayCount = sessionsOnDay(data, new Date()).filter(
    (s) => s.status === "planned",
  ).length;

  const sections = [
    <KpiRow key="kpis" />,
    <PackageReminderStrip key="pkg-reminders" />,
    <SessionsCalendar key="calendar" />,
    <div key="two-up" className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <UpcomingSessions />
      <ClientRoster />
    </div>,
    <ProgressSnapshot key="chart" />,
    // Wave 7: share the public marketing profile straight from the dashboard —
    // shows the live link (share/copy/view) or a set-up CTA into the editor.
    <ShareProfileCard
      key="share"
      publicUrl={account.data?.public_url}
      displayName={account.data?.display_name}
    />,
  ];

  return (
    <div className="space-y-6">
      {/* Greeting + at-a-glance banner */}
      <motion.div
        initial={reduce ? false : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 0.61, 0.36, 1] }}
        className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between"
      >
        <div>
          <p className="text-sm font-bold uppercase tracking-kicker text-coral">
            {t("dashboard.kicker")}
          </p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tightest text-body sm:text-4xl">
            {t(greetingKey())}
            {firstName ? `, ${firstName}` : ""}
          </h1>
          <p className="mt-1 text-body-soft">
            {t("dashboard.sessionsToday", { count: todayCount })}
          </p>
        </div>
        <Badge tone="success" solid className="self-start sm:self-auto">
          {t("dashboard.activeClients", { count: data.kpis.activeClients })}
        </Badge>
      </motion.div>

      {data.isError ? (
        <ErrorBlock onRetry={data.refetch} />
      ) : data.isLoading ? (
        <LoadingBlock label={t("common.loadingStudio")} />
      ) : (
        <>
          {data.kpis.dueToSchedule > 0 && (
            <Banner
              tone="warning"
              title={t("dashboard.dueToSchedule", {
                count: data.kpis.dueToSchedule,
              })}
            >
              {t("dashboard.dueToScheduleBlurb")}
            </Banner>
          )}

          {/* Wave 6: backend-driven "gone quiet" nudge. Self-hides when no
              client is inactive, so it never adds empty space on the grid. */}
          <InactiveClientsCard />

          {sections.map((section, i) => (
            <motion.div
              key={(section as { key?: string }).key ?? i}
              initial={reduce ? false : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.45,
                delay: reduce ? 0 : 0.06 * (i + 1),
                ease: [0.22, 0.61, 0.36, 1],
              }}
            >
              {section}
            </motion.div>
          ))}
        </>
      )}
    </div>
  );
}
